import { Outlet, NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import {
  LayoutDashboard, ClipboardList, ClipboardCheck, BarChart3,
  Upload, Settings, LogOut, Star, Users
} from 'lucide-react';

const NAV = [
  { to: '/',                icon: LayoutDashboard, label: 'Dashboard',          roles: null },
  { to: '/queue',           icon: ClipboardList,   label: 'Audit Queue',        roles: ['admin','auditor'] },
  { to: '/completed',       icon: ClipboardCheck,  label: 'Completed Audits',   roles: null },
  { to: '/tech-highlights', icon: Star,            label: 'Tech Highlights',    roles: null },
  { to: '/tech-stats',      icon: Users,           label: 'Technician Stats',   roles: null },
  { to: '/reports',         icon: BarChart3,       label: 'Reports',            roles: null },
  { to: '/upload',          icon: Upload,          label: 'Upload',             roles: ['admin'] },
  { to: '/admin',           icon: Settings,        label: 'Admin',              roles: ['admin'] },
];

const ROLE_COLORS = {
  admin:   'bg-orange-100 text-orange-800',
  auditor: 'bg-amber-100 text-amber-800',
  manager: 'bg-yellow-100 text-yellow-800',
};

export default function Layout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  return (
    <div className="flex h-screen bg-gray-50">
      {/* Sidebar */}
      <aside className="w-64 flex flex-col shadow-xl" style={{ background: 'linear-gradient(180deg, #3D2B1A 0%, #2A1E12 100%)' }}>

        {/* Logo */}
        <div className="px-4 py-4 border-b border-white/10 flex flex-col items-center gap-3">
          <div className="bg-white rounded-2xl px-3 py-2 w-full flex items-center justify-center shadow-md">
            <img src="/delair_logo.png" alt="Del-Air" className="h-24 object-contain" />
          </div>
          <div className="text-[#E8761A] text-xs font-bold tracking-widest uppercase">P&CC Audit System</div>
        </div>

        {/* Nav links */}
        <nav className="flex-1 px-3 py-4 space-y-1">
          {NAV.filter(n => !n.roles || (user?.role && n.roles.includes(user.role))).map(({ to, icon: Icon, label }) => (
            <NavLink
              key={to}
              to={to}
              end={to === '/'}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all
                ${isActive
                  ? 'bg-[#E8761A] text-white shadow-md'
                  : 'text-white/60 hover:text-white hover:bg-white/10'}`
              }
            >
              <Icon size={18} />
              {label}
            </NavLink>
          ))}
        </nav>

        {/* User info */}
        <div className="px-4 py-4 border-t border-white/10">
          <div className="flex items-center justify-between">
            <div className="min-w-0">
              <div className="text-white text-sm font-medium truncate">{user?.full_name || user?.name}</div>
              <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${ROLE_COLORS[user?.role] || ROLE_COLORS.auditor}`}>
                {user?.role}
              </span>
            </div>
            <button onClick={() => { logout(); navigate('/login'); }}
              className="text-white/40 hover:text-[#E8761A] ml-2 flex-shrink-0 transition-colors"
              title="Logout">
              <LogOut size={18} />
            </button>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <main className="flex-1 overflow-auto">
        <Outlet />
      </main>
    </div>
  );
}
